import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, CloudOff, CheckCircle2, Trash2, AlertTriangle, RefreshCw, ExternalLink, HardDrive, FileX,
} from "lucide-react";

const STATUS_META = {
  ok: { color: "emerald", icon: CheckCircle2, label: "OK", emoji: "✅" },
  missing: { color: "amber", icon: CloudOff, label: "Drive manquant", emoji: "⚠️" },
  trashed: { color: "amber", icon: Trash2, label: "Dans corbeille", emoji: "🗑️" },
  deleted: { color: "red", icon: FileX, label: "Supprimé du Drive", emoji: "❌" },
  api_error: { color: "zinc", icon: AlertTriangle, label: "API error", emoji: "⏸️" },
  local_gone: { color: "red", icon: FileX, label: "Fichier local perdu", emoji: "🗃️" },
};

export default function CRADriveHealthCheck() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("PROBLEMS");  // ALL / OK / PROBLEMS
  const [reuploadingId, setReuploadingId] = useState(null);
  const [onlySent, setOnlySent] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/cra-drive-health-check?only_sent=${onlySent}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du Health Check");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlySent]);

  const reupload = async (item) => {
    setReuploadingId(item.invoice_id);
    try {
      const r = await api.post(`/invoices/${item.invoice_id}/cra-drive-reupload`);
      toast.success(`CRA ${r.data?.number || item.number} re-uploadé sur Drive (${r.data?.parent_kind})`);
      // Refresh just this row
      setData((d) => ({
        ...d,
        items: d.items.map((it) => it.invoice_id === item.invoice_id
          ? { ...it, status: "ok", cra_drive_file_id: r.data.cra_drive_file_id, cra_drive_web_link: r.data.cra_drive_web_link, detail: "", remediation_available: false }
          : it),
        counters: { ...d.counters, ok: (d.counters.ok || 0) + 1, [item.status]: Math.max(0, (d.counters[item.status] || 0) - 1) },
      }));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Échec du re-upload"));
    } finally {
      setReuploadingId(null);
    }
  };

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    if (filter === "ALL") return data.items;
    if (filter === "OK") return data.items.filter((i) => i.status === "ok");
    return data.items.filter((i) => i.status !== "ok");
  }, [data, filter]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      <header className="space-y-1">
        <h1 className="text-2xl font-light tracking-tight flex items-center gap-2">
          <HardDrive size={22} /> CRA Drive Health Check
        </h1>
        <p className="text-sm text-zinc-600 max-w-3xl leading-relaxed">
          Audite la présence réelle sur Google Drive de chaque Monthly CRA persisté en base. Permet de remédier les CRAs « manquants » (générés avant Iter-207a) via un clic « Re-upload ».
        </p>
      </header>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={load}
          disabled={loading}
          data-testid="cra-drive-health-refresh"
          className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 text-xs font-mono uppercase tracking-wider text-zinc-700 rounded-md hover:bg-zinc-50 disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Relancer l'audit
        </button>
        <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer">
          <input
            type="checkbox"
            data-testid="cra-drive-health-only-sent"
            checked={onlySent}
            onChange={(e) => setOnlySent(e.target.checked)}
          />
          Factures envoyées uniquement
        </label>
        <div className="ml-auto flex items-center gap-1 text-xs text-zinc-500 font-mono">
          {data && `${data.scanned} scannées`}
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {Object.entries(data.counters || {}).map(([k, v]) => {
            const meta = STATUS_META[k] || { color: "zinc", label: k };
            const active = filter !== "ALL" && filter !== "OK" && k !== "ok"
              ? true
              : (filter === "OK" && k === "ok") || filter === "ALL";
            return (
              <button
                key={k}
                onClick={() => setFilter(k === "ok" ? "OK" : "PROBLEMS")}
                data-testid={`cra-drive-health-counter-${k}`}
                className={`text-left p-3 rounded-md border ${active ? "border-zinc-300 bg-zinc-50" : "border-zinc-100 bg-white"} hover:border-zinc-300`}
              >
                <div className={`text-[10px] font-mono uppercase tracking-wider text-${meta.color}-700`}>
                  {meta.emoji} {meta.label}
                </div>
                <div className="text-xl font-light text-zinc-800 mt-1">{v}</div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-2 text-xs">
        {["ALL", "PROBLEMS", "OK"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`cra-drive-health-filter-${f.toLowerCase()}`}
            className={`px-2.5 py-1 rounded font-mono uppercase tracking-wider ${filter === f ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
          >
            {f === "ALL" ? "Tout" : f === "OK" ? "OK uniquement" : "Problèmes uniquement"}
          </button>
        ))}
      </div>

      <div className="border border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-600 font-mono uppercase text-[10px] tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Facture</th>
              <th className="px-3 py-2 text-left">Mission</th>
              <th className="px-3 py-2 text-left">Période</th>
              <th className="px-3 py-2 text-left">Statut Drive</th>
              <th className="px-3 py-2 text-left">Détail</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {filteredItems.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-12 text-center text-zinc-500">
                {loading ? "Chargement…" : "Aucune ligne à afficher pour ce filtre."}
              </td></tr>
            )}
            {filteredItems.map((it) => {
              const meta = STATUS_META[it.status] || { color: "zinc", icon: AlertTriangle, label: it.status };
              const Icon = meta.icon;
              return (
                <tr key={it.invoice_id} data-testid={`cra-drive-health-row-${it.invoice_id}`}>
                  <td className="px-3 py-2 font-mono text-zinc-800">{it.number || it.invoice_id.slice(0, 12)}</td>
                  <td className="px-3 py-2 text-zinc-700">{it.mission_title || "—"}</td>
                  <td className="px-3 py-2 font-mono text-zinc-600">{it.year}-{String(it.month).padStart(2, "0")}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-${meta.color}-200 bg-${meta.color}-50 text-${meta.color}-700`}>
                      <Icon size={10} /> {meta.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-zinc-500 text-[11px] leading-snug max-w-[260px]">
                    {it.detail || "—"}
                    {it.cra_drive_web_link && (
                      <a href={it.cra_drive_web_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 ml-2 text-blue-600 hover:underline">
                        <ExternalLink size={9} /> Drive
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {it.remediation_available && (
                      <button
                        onClick={() => reupload(it)}
                        disabled={reuploadingId === it.invoice_id}
                        data-testid={`cra-drive-health-reupload-${it.invoice_id}`}
                        className="inline-flex items-center gap-1 px-2 h-7 border border-blue-200 bg-blue-50 text-blue-700 rounded text-[11px] font-mono uppercase hover:bg-blue-100 disabled:opacity-50"
                      >
                        {reuploadingId === it.invoice_id ? <Loader2 size={10} className="animate-spin" /> : <HardDrive size={10} />} Re-upload
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
